
import { Component, Vue } from "vue-property-decorator";
import * as BlogService from '@/services/BlogServices';
import { mixins } from "vue-class-component";
import { TipTap } from "@/mixins/TipTap";
import { ROOT_ACTIONS } from "@/store/actions";
import { askConfirm } from '@/content/confirm';
import * as StorageServices from '@/services/StorageServices';

// A blog video is streamed to phones over mobile data; a large raw recording
// cannot buffer and silently fails to play. Keep uploads small.
const MAX_VIDEO_MB = 50;

@Component({
})
export default class EditContent extends mixins(TipTap) {
    blog: any = null;
    sections = [] as any[];

    loading = false;

    loadingNewSection = '';

    updateKeys = {

    } as Record<string, number>;

    leadingKey = 0;

    /** Previews load once per visit and again after each upload, not on every keystroke. */
    loadedAt = Date.now();

    /** Paths not in this environment's bucket: staging's blogs are a copy of production, so older images are only on the CDN. */
    missingMedia = {} as Record<string, boolean>;

    media(path: string, key: number): string {
        const url = this.missingMedia[path] ? `https://cdn.decheckers.be/${path}?` : `${StorageServices.PublicUrl(path)}&`;
        return `${url}cache-bypass=${this.loadedAt}-${key}`;
    }

    mediaMissing(path: string) {
        this.$set(this.missingMedia, path, true);
    }

    uploaded(path: string) {
        this.$delete(this.missingMedia, path);
    }

    quoteImages = [
        {
            text: 'Groene Check',
            value: 'green-check'
        },
        {
            text: 'Rood Kruis',
            value: 'red-cross'
        }
    ]

    typeMappings = {
        'paragraph': 'Paragraaf',
        'banner': 'Banner',
        'image-text': 'Afbeelding + Tekst',
        'quote': 'Quote',
        'youtube': 'Youtube',
        'inline-images': 'Inline Afbeeldingen'
    } as Record<string, string>;

    async mounted() {
        const blogId = this.$route.params.id;
        this.blog = await BlogService.GetBlog(blogId);
        const sections = await BlogService.GetBlogSections(blogId);
        for (const section of sections) {
            this.updateKeys[section.id] = 0;
            // Older inline-image blocks have no alt texts yet; the fields bind to alts[i].
            if (section.type === 'inline-images' && !Array.isArray(section.options.alts)) section.options.alts = [];
        }
        this.sections = sections;
    }

    async addSection(type: string) {
        this.loadingNewSection = type;
        let options = {}

        if (type === 'image-text') {
            options = {
                textPosition: 'left',
                value: '',
                credits: '',
                alt: '',
            }
        }

        if (type === 'banner') {
            options = {
                credits: '',
                alt: '',
            }
        }

        if (type === 'video-text') {
            options = {
                textPosition: 'left',
                value: '',
                credits: '',
            }
        }

        if (type === 'quote') {
            options = {
                showImage: false,
                image: 'green-check'
            }
        }

        if (type === 'youtube') {
            options = {
                value: '',
                width: 560,
                height: 315
            }
        }

        if (type === 'inline-images') {
            options = {
                count: 2,
                credits: [],
                alts: [],
            }
        }

        const section = await BlogService.CreateBlogSection(this.blog.id, {
            type,
            options
        });
        this.updateKeys[section.id] = 0;
        this.sections.push(section);
        this.loadingNewSection = '';
    }

    async save() {
        this.loading = true;

        await BlogService.UpdateBlog(this.blog.id, this.blog);
        for (const section of this.sections) {
            await BlogService.SaveBlogSection(this.blog.id, section.id, section);
        }
        this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
            color: 'success',
            text: 'Content opgeslagen'
        })
        this.loading = false;
    }

    async saveLeadingImage() {
        const fileElement = document.getElementById('leading-image') as HTMLInputElement;
        if (!fileElement.files) return;
        const file = fileElement.files[0];
        await BlogService.SaveLeadingImage(this.blog.id, file);
        this.uploaded(`blog-content/${this.blog.id}/leading.png`);
        this.leadingKey++;
        this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
            color: 'success',
            text: 'Afbeelding opgeslagen'
        })
    }

    async saveBannerImage(sectionId: string) {
        const fileElement = document.getElementById(`banner-image-${sectionId}`) as HTMLInputElement;
        if (!fileElement.files) return;
        const file = fileElement.files[0];
        await BlogService.SaveBannerImage(this.blog.id, sectionId, file);
        this.uploaded(`blog-content/${this.blog.id}/sections/${sectionId}/banner`);
        this.updateKeys[sectionId]++;
        this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
            color: 'success',
            text: 'Afbeelding opgeslagen'
        })
    }

    async saveInlineImage(sectionId: string, index: number) {
        const fileElement = document.getElementById(`inline-image-${sectionId}-${index}`) as HTMLInputElement;
        if (!fileElement.files) return;
        const file = fileElement.files[0];
        await BlogService.SaveInlineImage(this.blog.id, sectionId, index, file);
        this.uploaded(`blog-content/${this.blog.id}/sections/${sectionId}/inline-image-${index}`);
        this.updateKeys[sectionId]++;
        this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
            color: 'success',
            text: 'Afbeelding opgeslagen'
        })
    }

    async saveImageText(sectionId: string) {
        const fileElement = document.getElementById(`image-text-${sectionId}`) as HTMLInputElement;
        if (!fileElement.files) return;
        const file = fileElement.files[0];
        await BlogService.SaveImageText(this.blog.id, sectionId, file);
        this.uploaded(`blog-content/${this.blog.id}/sections/${sectionId}/image-text`);
        this.updateKeys[sectionId]++;
        this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
            color: 'success',
            text: 'Afbeelding opgeslagen'
        })
    }

    async saveVideoText(sectionId: string) {
        const fileElement = document.getElementById(`video-text-${sectionId}`) as HTMLInputElement;
        if (!fileElement.files) return;
        const file = fileElement.files[0];
        if (!file) return;
        if (file.type !== 'video/mp4') {
            this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, { color: 'error', text: 'Kies een MP4-video (H.264).' });
            fileElement.value = '';
            return;
        }
        if (file.size > MAX_VIDEO_MB * 1024 * 1024) {
            this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
                color: 'error',
                text: `Video is te groot (${Math.round(file.size / 1024 / 1024)} MB, max ${MAX_VIDEO_MB} MB). Comprimeer de video (bv. HandBrake "Web"-preset) zodat ze vlot afspeelt op mobiel.`,
            });
            fileElement.value = '';
            return;
        }
        await BlogService.SaveVideoText(this.blog.id, sectionId, file);
        this.updateKeys[sectionId]++;
        this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
            color: 'success',
            text: 'Video opgeslagen'
        })
    }

    /** The caption track of a video block: video-text.vtt beside the video; `captions` tells the site it exists. */
    async saveVideoCaptions(section: { id: string; options: Record<string, unknown> }) {
        const fileElement = document.getElementById(`video-captions-${section.id}`) as HTMLInputElement;
        const file = fileElement.files?.[0];
        if (!file) return;
        if (!file.name.toLowerCase().endsWith('.vtt')) {
            this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, { color: 'error', text: 'Kies een WebVTT-bestand (.vtt).' });
            fileElement.value = '';
            return;
        }
        await BlogService.SaveVideoCaptions(this.blog.id, section.id, file);
        this.$set(section.options, 'captions', true);
        await BlogService.SaveBlogSection(this.blog.id, section.id, section);
        this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
            color: 'success',
            text: 'Ondertitels opgeslagen'
        })
    }

    async togglePublish() {
        this.blog.published = !this.blog.published;
        this.loading = true;
        await BlogService.UpdateBlog(this.blog.id, this.blog);
        this.loading = false;
    }

    async moveSection(index: number, direction: 'up' | 'down') {

        const section = this.sections[index];
        this.sections.splice(index, 1);
        this.sections.splice(direction === 'up' ? index - 1 : index + 1, 0, section);
        for (let i = 0; i < this.sections.length; i++) {
            this.sections[i].index = i;
        }

        for (const section of this.sections) {
            await BlogService.SaveBlogSection(this.blog.id, section.id, section);
        }
    }

    async deleteSection(sectionId: string) {
        if (!await askConfirm({ title: 'Sectie verwijderen?', text: 'De sectie wordt meteen verwijderd en kan niet teruggezet worden.', confirmLabel: 'Verwijderen', danger: true })) return;
        this.sections = this.sections.filter(e => e.id !== sectionId);
        await BlogService.DeleteBlogSection(this.blog.id, sectionId);
        // Reindex
        for (let i = 0; i < this.sections.length; i++) {
            this.sections[i].index = i;
        }
        for (const section of this.sections) {
            await BlogService.SaveBlogSection(this.blog.id, section.id, section);
        }
    }

    async openPreview() {
        window.open(`https://decheckers.be/blog/${this.blog.id}`, '_blank');
    }
}
